import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import type { Drill, DrillShotType } from '../types/lesson';
import { CLOUD_RUN_CONFIG } from '../config/cloudRun';

const DRILLS_COLLECTION = 'drills';

/**
 * Get all stock (platform-wide) drills, optionally filtered by shot type.
 */
export async function getStockDrills(shotType?: DrillShotType): Promise<Drill[]> {
  let q;
  if (shotType) {
    q = query(
      collection(db, DRILLS_COLLECTION),
      where('isStock', '==', true),
      where('shotType', '==', shotType),
      orderBy('title')
    );
  } else {
    q = query(
      collection(db, DRILLS_COLLECTION),
      where('isStock', '==', true),
      orderBy('shotType'),
      orderBy('title')
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ ...d.data(), id: d.id } as Drill));
}

/**
 * Get drills created by a specific coach.
 */
export async function getCoachDrills(coachId: string): Promise<Drill[]> {
  const q = query(
    collection(db, DRILLS_COLLECTION),
    where('coachId', '==', coachId),
    where('isStock', '==', false),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ ...d.data(), id: d.id } as Drill));
}

/**
 * Get all drills available to a coach (stock + their own custom drills).
 */
export async function getAllDrillsForCoach(coachId: string): Promise<Drill[]> {
  const [stock, custom] = await Promise.all([
    getStockDrills(),
    getCoachDrills(coachId),
  ]);
  return [...stock, ...custom];
}

/**
 * Get a single drill by ID.
 */
export async function getDrill(drillId: string): Promise<Drill | null> {
  const drillRef = doc(db, DRILLS_COLLECTION, drillId);
  const snap = await getDoc(drillRef);
  if (!snap.exists()) return null;
  return { ...snap.data(), id: snap.id } as Drill;
}

/**
 * Get multiple drills by their IDs (for displaying attached drills on a lesson).
 */
export async function getDrillsByIds(drillIds: string[]): Promise<Drill[]> {
  if (drillIds.length === 0) return [];

  // Firestore 'in' queries support max 30 items
  const results: Drill[] = [];
  const chunks = [];
  for (let i = 0; i < drillIds.length; i += 30) {
    chunks.push(drillIds.slice(i, i + 30));
  }

  for (const chunk of chunks) {
    const q = query(
      collection(db, DRILLS_COLLECTION),
      where('__name__', 'in', chunk)
    );
    const snapshot = await getDocs(q);
    results.push(...snapshot.docs.map((d) => ({ ...d.data(), id: d.id } as Drill)));
  }

  // Return in the same order as the input IDs
  return drillIds
    .map((id) => results.find((d) => d.id === id))
    .filter((d): d is Drill => d !== undefined);
}

/**
 * Create a new drill (stock or coach-custom).
 */
export async function createDrill(
  drillData: Omit<Drill, 'id' | 'createdAt'>
): Promise<string> {
  const drillRef = doc(collection(db, DRILLS_COLLECTION));
  const drillId = drillRef.id;

  await setDoc(drillRef, {
    ...drillData,
    id: drillId,
    createdAt: serverTimestamp(),
  });

  return drillId;
}

/**
 * Update an existing drill.
 */
export async function updateDrill(
  drillId: string,
  updates: Partial<Omit<Drill, 'id' | 'createdAt'>>
): Promise<void> {
  const drillRef = doc(db, DRILLS_COLLECTION, drillId);
  await updateDoc(drillRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a drill.
 */
export async function deleteDrill(drillId: string): Promise<void> {
  const drillRef = doc(db, DRILLS_COLLECTION, drillId);
  await deleteDoc(drillRef);
}

/**
 * Trigger Cloud Run to produce a web-optimized (≤1080p, fast-start) copy of a
 * drill video. Updates the `drills/{drillId}` document with the result and
 * returns the new URLs so callers can update local state without a re-fetch.
 */
export async function preprocessDrillVideoForWeb(
  drillId: string,
  sourcePath: string,
  videoUrl: string
): Promise<{ webOptimizedVideoUrl: string; webOptimizedStoragePath: string; durationSeconds: number }> {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('User not authenticated');

  const idToken = await currentUser.getIdToken();
  const response = await fetch(`${CLOUD_RUN_CONFIG.VIDEO_PROCESSOR_URL}/preprocess-drill-video`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ drillId, sourcePath, videoUrl }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || `Drill preprocessing failed: ${response.status}`);
  }

  const data = await response.json();
  return {
    webOptimizedVideoUrl: data.webOptimizedVideoUrl,
    webOptimizedStoragePath: data.webOptimizedStoragePath,
    durationSeconds: data.durationSeconds,
  };
}

/**
 * Get all drills regardless of type or ownership (admin use only).
 * Sorted: stock first, then by shotType, then title.
 */
export async function getAllDrills(): Promise<Drill[]> {
  const snapshot = await getDocs(collection(db, DRILLS_COLLECTION));
  return snapshot.docs
    .map((d) => ({ ...d.data(), id: d.id } as Drill))
    .sort((a, b) => {
      if (a.isStock !== b.isStock) return a.isStock ? -1 : 1;
      if (a.shotType !== b.shotType) return a.shotType.localeCompare(b.shotType);
      return a.title.localeCompare(b.title);
    });
}
